import { css } from '@eti/styles';
import { breakpoints, Inline, Text, textVariants, vars } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import type { BaggageInfo } from '../../resultTripCards/components/TripBaggageInfo';
import { routes } from '../../../../../../constants/routesAndApisConstants';
import IncludedBaggage from './IncludedBaggage';
import PossibleToAddBaggage from './PossibleToAddBaggage';
import { possibleToAddBaggages } from './utils';
import type { NotPossibleToAddBaggageType } from './NotPossibleToAddBaggage';
import NotPossibleToAddBaggage from './NotPossibleToAddBaggage';

const sectionWrapperStyles = css`
  flex-direction: column;
`;

const baggageSectionWrapperStyles = (isInDrawer: boolean) => css`
  width: 100%;
  ${isInDrawer
    ? `& div:nth-child(3) {
    width: 100%;
  }`
    : `flex-direction: column;
    & > div {padding-bottom: 16px; border-bottom: 1px solid ${vars.colors.divider}};
    & > div:last-child {padding-bottom: 0; border-bottom: none};`}
`;

const inlineStyles = (isInDrawer: boolean) => css`
  ${isInDrawer && `margin-top: 16px`};
`;

const textStyles = css`
  border-top: 1px solid ${vars.colors.divider};
  padding-top: 16px;
`;

export interface BaggageInformationBodyProps {
  baggageInfo: BaggageInfo;
  isResultCardBaggageEnabled: boolean;
  isInDrawer?: boolean;
  isVI?: boolean;
  hasLessThan2hSelfTransfer?: boolean;
}

export const BaggageInformationBody = ({
  baggageInfo,
  isResultCardBaggageEnabled,
  isInDrawer = false,
  isVI = false,
  hasLessThan2hSelfTransfer,
}: BaggageInformationBodyProps) => {
  const { t } = useTranslation();
  const { availableExtraProducts, includedCabinBaggage, includedCheckedBaggage } = baggageInfo;
  const isChangeTripPage = window.location.pathname.includes(routes.CHANGE_TRIP);
  const isPersonalItemIncluded = includedCabinBaggage?.includedPersonalItem;
  const isCabinBaggageIncluded = Boolean(includedCabinBaggage?.pieces);
  const isCheckedBaggageIncluded = Boolean(includedCheckedBaggage?.weight);
  const isIncludedSectionEnabled = Boolean(
    isPersonalItemIncluded || isCabinBaggageIncluded || isCheckedBaggageIncluded,
  );

  const { listOfPossibleToAdd, hasCheckInBaggage, hasCabinBaggage } =
    possibleToAddBaggages(availableExtraProducts);

  const isPossibleToAddInNextStepSectionEnabled =
    !isChangeTripPage && listOfPossibleToAdd.length > 0;

  const getNotPossibleToAddBaggages = () => {
    const baggageConditions = [
      {
        condition: !includedCabinBaggage?.includedPersonalItem,
        text: t('Result.Baggage.PersonalItem.Text'),
        baggageIcon: 'personal-item',
        isCheckedInBaggage: false,
      },
      {
        condition: !isCabinBaggageIncluded && !hasCabinBaggage,
        text: t('Result.Baggage.CabinBaggage.Text'),
        baggageIcon: 'cabin-baggage',
        isCheckedInBaggage: false,
      },
      {
        condition: !isCheckedBaggageIncluded && !hasCheckInBaggage,
        text: t('Result.Baggage.CheckedBaggage.Text'),
        baggageIcon: 'check-in-baggage',
        isCheckedInBaggage: true,
      },
    ];

    return baggageConditions.reduce(
      (
        acc: NotPossibleToAddBaggageType[],
        { condition, text, baggageIcon, isCheckedInBaggage },
      ) => {
        if (condition) {
          acc.push({ text, baggageIcon, isCheckedInBaggage });
        }
        return acc;
      },
      [],
    );
  };

  const notPossibleToAddBaggageList = getNotPossibleToAddBaggages();
  const isNotPossibleToAddWithUsSectionEnabled = Boolean(notPossibleToAddBaggageList.length);
  const isDividerOrResultCardBaggageEnabled =
    (isIncludedSectionEnabled &&
      isPossibleToAddInNextStepSectionEnabled &&
      isNotPossibleToAddWithUsSectionEnabled) ||
    isResultCardBaggageEnabled;

  return (
    <Inline
      align="start"
      className={isResultCardBaggageEnabled ? sectionWrapperStyles : inlineStyles(isInDrawer)}
      collapseBelow={breakpoints._768}
    >
      <Inline className={baggageSectionWrapperStyles(isInDrawer)} collapseBelow={breakpoints._768}>
        {isIncludedSectionEnabled && (
          <IncludedBaggage
            includedCabinBaggage={includedCabinBaggage}
            includedCheckedBaggage={includedCheckedBaggage}
            isResultCardBaggageEnabled={isResultCardBaggageEnabled}
            isVI={isVI}
          />
        )}
        {isPossibleToAddInNextStepSectionEnabled && (
          <PossibleToAddBaggage
            hasCheckInBaggage={hasCheckInBaggage}
            isInDrawer={isInDrawer}
            isIncludedSectionEnabled={isIncludedSectionEnabled}
            isResultCardBaggageEnabled={isResultCardBaggageEnabled}
            isVI={isVI}
            possibleToAddBaggages={listOfPossibleToAdd}
          />
        )}
        {isNotPossibleToAddWithUsSectionEnabled && (
          <NotPossibleToAddBaggage
            hasLessThan2hSelfTransfer={hasLessThan2hSelfTransfer}
            isDividerOrResultCardBaggageEnabled={isDividerOrResultCardBaggageEnabled}
            isInDrawer={isInDrawer}
            isIncludedSectionEnabled={isIncludedSectionEnabled}
            isResultCardBaggageEnabled={isResultCardBaggageEnabled}
            notPossibleToAddBaggageList={notPossibleToAddBaggageList}
          />
        )}
      </Inline>
      <Text className={textStyles} variant={textVariants._14}>
        {t('Result.Baggage.Tooltip.Text')}
      </Text>
    </Inline>
  );
};
